import type { ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { DeltaLabel } from '@/shared/components';
import {
  DEFAULT_BASE_ASSET,
  HIDDEN_BALANCES_FULL_TEXT,
  NUMERIC_CONFIG,
} from '@/shared/config';
import type { BaseAsset } from '@/shared/types/assetsTypes';
import { applyAmountPrecision, formatReceivedAmount } from '@/shared/lib';
import { cn } from '@/shared/utils/styling';
import { useAccountDetailsActionButtons } from '@/widgets/account-details';
import {
  ACCOUNT_TYPES,
  type AccountMarginRest,
  type AccountRest,
  type AccountType,
  useAccountsStore,
} from '@/entities/account';
import { useAccountCreateModal } from '@/widgets/account-create';
import ArrowDownIcon from '@/shared/assets/arrow-down-icon.svg?react';
import TransferIcon from '@/shared/assets/arrows-down-up-icon.svg?react';
import { AccountCardMenu } from './AccountCardMenu';
import { AccountsListPopover } from './AccountsListPopover';
import { AccountsTransferPopover } from './AccountsTransferPopover';

const PRIMARY_BUTTON_CLASSNAME =
  'flex h-[3.2rem] items-center gap-[0.4rem] pl-[0.8rem] pr-[1.2rem] rounded-[1rem] headline5 text-secondary hover:bg-stroke-bg hover:text-primary cursor-pointer transition-colors';

interface AccountCardProps {
  isBalancesHidden: boolean;
  type: AccountType;
  accountTitle: string;
  icon: ElementType;
  balance: number | null;
  currency?: BaseAsset;
  deltaPercent: number | null;
  precision?: number;
  isPending?: boolean;
  className?: string;
  onDeposit: () => void;
  onWithdraw: () => void;
  onInternalTransfer: (context?: {
    accountType: AccountType;
    accountNumber: string;
    assetCode: string;
    direction?: 'from' | 'to';
  }) => void;
  accounts: Array<AccountRest | AccountMarginRest>;
  onAccountClick: (accountNumber: string) => void;
  onExchange: () => void;
}

const AccountCard = ({
  isBalancesHidden,
  type,
  accountTitle,
  icon,
  balance,
  currency = DEFAULT_BASE_ASSET,
  deltaPercent,
  precision = NUMERIC_CONFIG.precision.default,
  className,
  isPending,
  onDeposit,
  onWithdraw,
  onInternalTransfer,
  accounts,
  onAccountClick,
  onExchange,
}: AccountCardProps) => {
  const { t } = useTranslation();

  const { actionButtonsVisibility } = useAccountDetailsActionButtons(type);
  const accountData = useAccountsStore(state => state[type]);
  const { openCreateAccountModal } = useAccountCreateModal(type);

  const Icon = icon;
  const isWallet = type === 'ewallet';

  const tradingAccounts = accounts.filter(
    account => account.accTypeName === ACCOUNT_TYPES.Trading
  );
  const hasSeveralAccounts = tradingAccounts.length > 1;

  const getTransferAssetCode = (accountNumber: string) => {
    const account = accounts.find(item => item.accNum === accountNumber);

    return account && 'balanceAsset' in account
      ? (account.balanceAsset?.code ?? '')
      : '';
  };

  const openTransfer = (accountNumber: string, direction?: 'from' | 'to') =>
    onInternalTransfer({
      accountType: type,
      accountNumber,
      assetCode: getTransferAssetCode(accountNumber),
      direction,
    });

  const handleTransfer = (direction?: 'from' | 'to') =>
    openTransfer(
      accountData?.accounts?.length === 1
        ? accountData?.accounts[0]?.accNum
        : '',
      direction
    );

  const PrimaryIcon = isWallet ? ArrowDownIcon : TransferIcon;
  const primaryLabel = isWallet
    ? t('personalAccount.overview.actions.deposit')
    : t('personalAccount.overview.actions.transfer');
  const isPrimaryVisible = isWallet
    ? actionButtonsVisibility?.deposit
    : actionButtonsVisibility?.transfer;

  const balanceText = isBalancesHidden
    ? `${HIDDEN_BALANCES_FULL_TEXT} ${currency}`
    : balance != null
      ? `${formatReceivedAmount(applyAmountPrecision(balance, precision, 'credit'))} ${currency}`
      : '';

  return (
    <div
      className={cn(
        'flex flex-col gap-[0.4rem]',
        'w-full min-w-[18rem] max-w-[22rem]',
        'pb-[0.6rem] rounded-[1.6rem] bg-secondary-bg',
        className
      )}>
      <div className="flex flex-col gap-[1.2rem] p-[1.6rem] pl-[2rem] rounded-[1.6rem] bg-background ring-1 ring-inset ring-stroke shadow-card">
        <div className="flex h-[3.2rem] items-start justify-between gap-[1.2rem]">
          {isWallet ? (
            <Icon className="size-[3.2rem]" />
          ) : (
            <span className="size-[3.2rem] rounded-full bg-secondary-bg border border-stroke-bg flex items-center justify-center shrink-0">
              <Icon className="size-[2rem] text-primary" />
            </span>
          )}

          <DeltaLabel
            deltaPercent={deltaPercent}
            size="card"
            showLabelText={false}
            className="h-[2rem] pl-[0.4rem] pr-[0.8rem] py-[0.2rem]"
            deltaPositiveClassName="bg-primary-cyan/8 text-primary-cyan"
            deltaNegativeClassName="bg-red/8 text-red"
            arrowPositiveClassName="text-primary-cyan"
          />
        </div>

        <div className="flex flex-col gap-[0.2rem]">
          <div className="flex h-[2.2rem] items-center gap-[1rem]">
            <span className="medium5 text-primary truncate">
              {accountTitle}
            </span>

            {hasSeveralAccounts && (
              <AccountsListPopover
                type={type}
                accounts={tradingAccounts}
                currency={currency}
                precision={precision}
                isBalancesHidden={isBalancesHidden}
                onTransfer={accountNumber => openTransfer(accountNumber)}
                onAccountClick={onAccountClick}
                onAddAccount={
                  actionButtonsVisibility?.openNewAccount
                    ? () => openCreateAccountModal()
                    : undefined
                }
              />
            )}
          </div>

          <span
            className={cn(
              'h-[2rem] medium5 text-secondary truncate',
              isPending && 'dots-animation'
            )}>
            {isPending ? t('openAccount.pending') : balanceText}
          </span>
        </div>
      </div>

      <div className="flex h-[3.2rem] items-center justify-between gap-[0.8rem] px-[0.8rem]">
        {isPrimaryVisible ? (
          !isWallet && hasSeveralAccounts ? (
            <AccountsTransferPopover
              accounts={tradingAccounts}
              currency={currency}
              precision={precision}
              isBalancesHidden={isBalancesHidden}
              onSelect={accountNumber => openTransfer(accountNumber)}>
              <button
                type="button"
                className={cn(
                  PRIMARY_BUTTON_CLASSNAME,
                  'data-[state=open]:bg-stroke-bg data-[state=open]:text-primary'
                )}>
                <PrimaryIcon className="size-[1.6rem]" />
                {primaryLabel}
              </button>
            </AccountsTransferPopover>
          ) : (
            <button
              type="button"
              onClick={isWallet ? onDeposit : () => handleTransfer()}
              className={PRIMARY_BUTTON_CLASSNAME}>
              <PrimaryIcon className="size-[1.6rem]" />
              {primaryLabel}
            </button>
          )
        ) : (
          <span />
        )}

        {actionButtonsVisibility && (
          <AccountCardMenu
            type={type}
            actionButtonsVisibility={actionButtonsVisibility}
            onDeposit={onDeposit}
            onWithdraw={onWithdraw}
            onInternalTransfer={handleTransfer}
            onAccountTransfer={accountNumber => openTransfer(accountNumber)}
            onOpenNewAccount={() => openCreateAccountModal()}
            onExchange={onExchange}
            accountTitle={accountTitle}
            accounts={tradingAccounts}
            currency={currency}
            precision={precision}
            isBalancesHidden={isBalancesHidden}
            onAccountClick={onAccountClick}
            triggerClassName="static size-[3.2rem] rounded-[1rem] text-secondary"
          />
        )}
      </div>
    </div>
  );
};

export { AccountCard };
